import { DateTime } from 'luxon';
import { parseDate, DateValue } from '@internationalized/date';

export const formatFutureDate = (date: DateTime) => {
  const dt = date.startOf('day');
  const now = DateTime.now()
    .setZone(date.zoneName || 'America/New_York')
    .startOf('day');
  const diff = dt.diff(now, ['days']).toFormat('d');

  if (parseInt(diff) === 0) {
    return 'Today';
  }
  if (parseInt(diff) === 1) {
    return 'Tomorrow';
  }
  if (parseInt(diff) <= 7) {
    return dt.toLocaleString({
      weekday: 'long',
    });
  }
  return dt.toLocaleString({
    day: '2-digit',
    month: 'long',
  });
};

export const isThisWeek = (date: string) => {
  const dateDiff = parseInt(
    DateTime.fromISO(date).diff(DateTime.now(), ['days']).toFormat('d')
  );
  return dateDiff <= 6 && dateDiff >= 0;
};

export const afterThisWeek = (date?: string | null) => {
  if (!date) return true;
  const dateDiff = parseInt(
    DateTime.fromISO(date).diff(DateTime.now(), ['days']).toFormat('d')
  );
  return dateDiff >= 7;
};

export const isWithinHoursLimit = (date: string, cutOff: number) => {
  const dateDiff = parseInt(
    DateTime.fromISO(date).diff(DateTime.now(), ['minutes']).toFormat('m')
  );
  const pastHourLimit = -(cutOff * 60); // convert cutOff to minutes
  return dateDiff > pastHourLimit;
};

export const isFutureDatedWithCutoff = (
  cutOff: number,
  date?: string | null
) => {
  if (!date) return false;
  const now = DateTime.now();
  const dateToTest = DateTime.fromISO(date);
  return now <= dateToTest || isWithinHoursLimit(date, cutOff);
};

export const isLastSixMonths = (date: string) => {
  const now = DateTime.now();
  const pastDate = DateTime.fromISO(date);

  const monthDiff = now.diff(pastDate, ['months']).toObject().months;

  return monthDiff && monthDiff <= 6;
};

export const getShortTimeZone = (
  date: string,
  timeZone: string | undefined
) => {
  return DateTime.fromISO(date, {
    zone: timeZone,
    locale: 'en',
  }).toFormat('ZZZZ');
};

export const longDateFormat = (date: DateTime) => {
  return date.toLocaleString({
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

export const getTodayDateString = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Converts a `@internationalized/date` DateValue to a "YYYY-MM-DD" string
 * suitable for persisting in form state and sending to the API.
 */
export const formatDateValue = (date: DateValue): string => {
  return `${date.year}-${String(date.month).padStart(2, '0')}-${String(
    date.day
  ).padStart(2, '0')}`;
};

/**
 * Resolves a default-date config value (e.g. "Today") into a concrete `DateValue`.
 * Currently only the literal string "Today" is supported; anything else
 * (including `undefined`) yields `null` — meaning no default is applied.
 */
export const getDefaultDateValue = (
  defaultDate: string | undefined
): DateValue | null => {
  if (defaultDate === 'Today') {
    return parseDate(getTodayDateString());
  }
  return null;
};
